import html2pdf from "html2pdf.js";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";

/**
 * Extracts plain text from HTML string
 * @param {string} html - HTML string to extract text from
 * @returns {string} - Plain text content
 */
export const extractPlainText = (html = "") => {
  if (typeof window === "undefined") return html || "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
};

/**
 * Sanitizes a filename by removing invalid characters
 * @param {string} title - The title to sanitize
 * @param {string} ext - File extension (default: "pdf")
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (title = "", ext = "pdf") => {
  const safe = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${safe || "lawfusion_document"}.${ext}`;
};

const isIOSLikeDevice = () => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

/**
 * Saves a Blob as a file. On iOS Safari, `<a download>` with blob URLs often
 * navigates in-place or shows a broken prompt; Web Share opens the system sheet
 * so users can save to Files / share elsewhere.
 */
export async function saveBlobWithPlatformSupport(blob, filename, mimeType) {
  const typedBlob = new Blob([blob], { type: mimeType });
  const file = new File([typedBlob], filename, { type: mimeType });
  const isIOS = isIOSLikeDevice();

  if (
    isIOS &&
    typeof navigator !== "undefined" &&
    navigator.share &&
    navigator.canShare &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: filename,
      });
      return;
    } catch (err) {
      if (err && err.name === "AbortError") {
        return;
      }
      console.warn("Web Share failed, using fallback:", err);
    }
  }

  const ua = navigator.userAgent.toLowerCase();
  const isFirefox = ua.indexOf("firefox") > -1;

  if (isIOS && mimeType === "application/pdf") {
    const blobUrl = URL.createObjectURL(typedBlob);
    const opened = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (opened) {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
      return;
    }
    URL.revokeObjectURL(blobUrl);
  }

  const blobUrl = URL.createObjectURL(typedBlob);

  if (isFirefox && mimeType === "application/pdf") {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function (e) {
        const dataUrl = e.target.result;

        const iframe = document.createElement("iframe");
        iframe.style.cssText =
          "display:none;width:0;height:0;border:none;position:absolute;left:-9999px;";
        document.body.appendChild(iframe);

        const attemptDownload = () => {
          try {
            const iframeDoc =
              iframe.contentDocument || iframe.contentWindow.document;
            if (!iframeDoc) {
              setTimeout(attemptDownload, 50);
              return;
            }

            iframeDoc.open();
            iframeDoc.write(
              "<!DOCTYPE html><html><head><title>Download</title></head><body></body></html>",
            );
            iframeDoc.close();

            const link = iframeDoc.createElement("a");
            link.href = dataUrl;
            link.download = filename;
            link.style.display = "none";
            iframeDoc.body.appendChild(link);

            const clickEvent = new MouseEvent("click", {
              view: window,
              bubbles: true,
              cancelable: false,
            });
            link.dispatchEvent(clickEvent);

            setTimeout(() => {
              try {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              } catch (cleanupError) {
                console.warn("Error cleaning up iframe:", cleanupError);
              }
              URL.revokeObjectURL(blobUrl);
              resolve();
            }, 1000);
          } catch (error) {
            console.warn(
              "Iframe download failed, using fallback method:",
              error,
            );
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            link.style.display = "none";
            document.body.appendChild(link);

            link.click();

            setTimeout(() => {
              try {
                if (document.body.contains(link)) {
                  document.body.removeChild(link);
                }
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              } catch (cleanupError) {
                console.warn("Error cleaning up fallback:", cleanupError);
              }
              URL.revokeObjectURL(blobUrl);
              resolve();
            }, 1000);
          }
        };

        setTimeout(attemptDownload, 100);
      };
      reader.onerror = function () {
        console.warn("FileReader failed, using blob URL directly");
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
          resolve();
        }, 100);
      };
      reader.readAsDataURL(typedBlob);
    });
  }

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, isIOS ? 2000 : 100);
}

/**
 * Downloads content as PDF
 * @param {string} editorContent - HTML to render into the PDF
 * @param {string} editorTitle - Title, used for the filename
 * @param {object} [options]
 * @param {boolean} [options.styled] - The content carries its own complete
 *   styling (colours, cards, spacing), so skip the document stylesheet below
 *   that flattens everything to serif black-on-white. Used by the consumer
 *   summary, whose PDF is meant to look like the coloured blocks on screen.
 */
export const handleDownloadPDF = async (
  editorContent,
  editorTitle,
  options = {},
) => {
  if (typeof window === "undefined") return;

  const styled = Boolean(options.styled);

  // Keep content width slightly under printable width to avoid right-edge glyph clipping.
  const PDF_CONTENT_WIDTH_IN = 7.2;
  const PDF_CONTENT_WIDTH_PX = Math.ceil(PDF_CONTENT_WIDTH_IN * 96);

  const wrapper = document.createElement("div");
  wrapper.id = "pdf-export-wrapper";
  wrapper.style.cssText = `
    position: fixed;
    left: -9999px;
    top: 0;
    width: ${PDF_CONTENT_WIDTH_IN}in;
    max-width: ${PDF_CONTENT_WIDTH_IN}in;
    visibility: hidden;
    pointer-events: none;
    overflow: visible;
  `;

  const element = document.createElement("div");
  element.id = "pdf-export-content";
  element.innerHTML = editorContent || "<p></p>";

  element.style.cssText = `
    background: #ffffff !important;
    color: #1f2937 !important;
    padding: 0;
    margin: 0;
    width: ${PDF_CONTENT_WIDTH_IN}in;
    max-width: ${PDF_CONTENT_WIDTH_IN}in;
    font-family: ${
      styled
        ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        : "'Times New Roman', Times, serif"
    };
    font-size: ${styled ? "11pt" : "12pt"};
    line-height: 1.6;
    word-spacing: ${styled ? "normal" : "0.05em"};
    letter-spacing: ${styled ? "normal" : "0.01em"};
    overflow: visible;
  `;

  const style = document.createElement("style");
  style.textContent = `
    #pdf-export-content * {
      box-sizing: border-box;
      word-wrap: normal;
      overflow-wrap: normal;
      hyphens: none;
      word-break: normal;
      max-width: 100%;
    }
    #pdf-export-content div {
      background: #ffffff !important;
      color: #1f2937 !important;
      word-spacing: 0.05em;
      letter-spacing: 0.01em;
      word-wrap: normal;
      overflow-wrap: normal;
      word-break: normal;
      hyphens: none;
      max-width: 100%;
    }
    #pdf-export-content p {
      color: #1f2937 !important;
      margin: 0.2em 0;
      white-space: normal;
      word-spacing: 0.05em;
      letter-spacing: 0.01em;
      line-height: 1.6;
      text-align: justify;
      text-justify: inter-word;
      word-wrap: normal;
      overflow-wrap: normal;
      word-break: normal;
      hyphens: none;
      page-break-inside: avoid;
      max-width: 100%;
    }
    #pdf-export-content h1, 
    #pdf-export-content h2, 
    #pdf-export-content h3, 
    #pdf-export-content h4, 
    #pdf-export-content h5, 
    #pdf-export-content h6 {
      color: #111827 !important;
      font-weight: bold;
      margin: 0.4em 0 0.15em 0;
      word-spacing: 0.05em;
      letter-spacing: 0.02em;
      line-height: 1.2;
      word-wrap: normal;
      overflow-wrap: normal;
      word-break: normal;
      hyphens: none;
      page-break-after: avoid;
      page-break-inside: avoid;
      max-width: 100%;
    }
    #pdf-export-content h1 { font-size: 24pt; }
    #pdf-export-content h2 { font-size: 20pt; }
    #pdf-export-content h3 { font-size: 18pt; }
    #pdf-export-content h4 { font-size: 16pt; }
    #pdf-export-content h5 { font-size: 14pt; }
    #pdf-export-content h6 { font-size: 12pt; }
    #pdf-export-content h1 + p,
    #pdf-export-content h2 + p,
    #pdf-export-content h3 + p,
    #pdf-export-content h4 + p,
    #pdf-export-content h5 + p,
    #pdf-export-content h6 + p {
      margin-top: 0.1em;
    }
    #pdf-export-content h1 + ul,
    #pdf-export-content h2 + ul,
    #pdf-export-content h3 + ul,
    #pdf-export-content h4 + ul,
    #pdf-export-content h5 + ul,
    #pdf-export-content h6 + ul,
    #pdf-export-content h1 + ol,
    #pdf-export-content h2 + ol,
    #pdf-export-content h3 + ol,
    #pdf-export-content h4 + ol,
    #pdf-export-content h5 + ol,
    #pdf-export-content h6 + ol {
      margin-top: 0.15em;
    }
    #pdf-export-content strong, 
    #pdf-export-content b {
      color: #111827 !important;
      font-weight: bold;
      letter-spacing: 0.01em;
    }
    #pdf-export-content em, 
    #pdf-export-content i {
      color: #1f2937 !important;
      font-style: italic;
      letter-spacing: 0.01em;
    }
    #pdf-export-content ul, 
    #pdf-export-content ol {
      color: #1f2937 !important;
      margin: 0.3em 0;
      padding-left: 2em;
      page-break-inside: auto;
      max-width: 100%;
    }
    #pdf-export-content ul {
      list-style-type: disc;
    }
    #pdf-export-content ol {
      list-style-type: decimal;
    }
    #pdf-export-content li {
      color: #1f2937 !important;
      margin: 0.1em 0;
      padding-left: 0.5em;
      word-spacing: 0.05em;
      letter-spacing: 0.01em;
      line-height: 1.5;
      word-wrap: normal;
      overflow-wrap: normal;
      word-break: normal;
      hyphens: none;
      page-break-inside: avoid;
      max-width: 100%;
    }
    #pdf-export-content a {
      color: #2563eb !important;
      text-decoration: underline;
      word-spacing: 0.05em;
    }
    #pdf-export-content blockquote {
      color: #374151 !important;
      border-left: 4px solid #d1d5db;
      padding-left: 1em;
      margin: 0.4em 0;
      word-spacing: 0.05em;
      letter-spacing: 0.01em;
      page-break-inside: avoid;
      max-width: 100%;
    }
    #pdf-export-content code {
      background: #f3f4f6 !important;
      color: #1f2937 !important;
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      word-spacing: normal;
      letter-spacing: normal;
    }
    #pdf-export-content pre {
      background: #f3f4f6 !important;
      color: #1f2937 !important;
      padding: 0.8em;
      margin: 0.3em 0;
      border-radius: 4px;
      overflow-x: auto;
      white-space: pre-wrap;
      word-wrap: break-word;
      page-break-inside: avoid;
      max-width: 100%;
    }
    #pdf-export-content table {
      border-collapse: collapse;
      width: 100%;
      max-width: 100%;
      margin: 0.4em 0;
      page-break-inside: avoid;
    }
    #pdf-export-content th, 
    #pdf-export-content td {
      border: 1px solid #d1d5db !important;
      padding: 0.5em;
      color: #1f2937 !important;
      word-spacing: 0.05em;
      letter-spacing: 0.01em;
      max-width: 100%;
      overflow-wrap: break-word;
    }
    #pdf-export-content th {
      background: #f9fafb !important;
      font-weight: bold;
      color: #111827 !important;
    }
  `;
  // Styled content brings its own rules; this sheet would override them with
  // !important and strip the colour back out. It still needs a guard, though:
  // the export node lives on document.body, so the app's global rules reach it
  // — anything animating or faded on screen must be solid on paper.
  const guard = document.createElement("style");
  guard.textContent = `
    #pdf-export-content, #pdf-export-content * {
      opacity: 1 !important;
      filter: none !important;
      animation: none !important;
      transition: none !important;
      text-shadow: none !important;
      visibility: visible !important;
    }
  `;
  if (styled) document.head.appendChild(guard);
  else document.head.appendChild(style);

  wrapper.appendChild(element);
  document.body.appendChild(wrapper);

  await new Promise((r) => setTimeout(r, 200));

  const elementHeight = element.offsetHeight;
  console.log(`Generating PDF for content with height: ${elementHeight}px`);

  const opt = {
    margin: [0.75, 0.6, 0.75, 0.6],
    filename: sanitizeFilename(editorTitle, "pdf"),
    image: {
      type: "jpeg",
      quality: 0.98,
    },
    html2canvas: {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      letterRendering: true,
      allowTaint: false,
      imageTimeout: 0,
      scrollY: 0,
      scrollX: 0,
      width: PDF_CONTENT_WIDTH_PX,
      windowWidth: PDF_CONTENT_WIDTH_PX,
    },
    jsPDF: {
      unit: "in",
      format: "letter",
      orientation: "portrait",
      compress: true,
      precision: 16,
    },
    pagebreak: {
      mode: ["css", "legacy"],
      before: ".page-break-before",
      after: ".page-break-after",
      avoid: [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "li",
        "img",
        "table",
        "pre",
        "blockquote",
      ],
    },
    enableLinks: true,
  };

  try {
    const pdf = await html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get("pdf");
    const pageCount = pdf.internal.getNumberOfPages();
    console.log(`PDF generated with ${pageCount} page(s)`);

    const blob = pdf.output("blob");
    const filename = sanitizeFilename(editorTitle, "pdf");
    const pdfBlob = new Blob([blob], { type: "application/pdf" });
    await saveBlobWithPlatformSupport(pdfBlob, filename, "application/pdf");
  } catch (error) {
    console.error("PDF generation error:", error);
    throw error;
  } finally {
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
    if (document.head.contains(guard)) {
      document.head.removeChild(guard);
    }
    if (document.body.contains(wrapper)) {
      document.body.removeChild(wrapper);
    }
  }
};

/**
 * Downloads content as DOCX
 * @param {string} editorContent - HTML content to export
 * @param {string} editorTitle - Title for the DOCX file
 * @param {function} showError - Error notification function
 */
export const handleDownloadDoc = async (
  editorContent,
  editorTitle,
  showError,
) => {
  if (typeof window === "undefined") return;

  try {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = editorContent || "<p></p>";

    const convertHtmlToDocx = (element) => {
      const children = Array.from(element.childNodes);
      const paragraphs = [];

      for (const node of children) {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            paragraphs.push(
              new Paragraph({
                children: [new TextRun(text)],
                spacing: { after: 200 },
              }),
            );
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();
          const text = node.textContent?.trim() || "";

          if (!text) continue;

          switch (tagName) {
            case "h1":
              paragraphs.push(
                new Paragraph({
                  text: text,
                  heading: HeadingLevel.HEADING_1,
                  spacing: { after: 240, before: 240 },
                }),
              );
              break;
            case "h2":
              paragraphs.push(
                new Paragraph({
                  text: text,
                  heading: HeadingLevel.HEADING_2,
                  spacing: { after: 200, before: 200 },
                }),
              );
              break;
            case "h3":
              paragraphs.push(
                new Paragraph({
                  text: text,
                  heading: HeadingLevel.HEADING_3,
                  spacing: { after: 180, before: 180 },
                }),
              );
              break;
            case "h4":
              paragraphs.push(
                new Paragraph({
                  text: text,
                  heading: HeadingLevel.HEADING_4,
                  spacing: { after: 160, before: 160 },
                }),
              );
              break;
            case "h5":
              paragraphs.push(
                new Paragraph({
                  text: text,
                  heading: HeadingLevel.HEADING_5,
                  spacing: { after: 140, before: 140 },
                }),
              );
              break;
            case "h6":
              paragraphs.push(
                new Paragraph({
                  text: text,
                  heading: HeadingLevel.HEADING_6,
                  spacing: { after: 120, before: 120 },
                }),
              );
              break;
            case "p":
              const pChildren = [];
              const processNode = (n) => {
                if (n.nodeType === Node.TEXT_NODE) {
                  const txt = n.textContent.trim();
                  if (txt) pChildren.push(new TextRun(txt));
                } else if (n.nodeType === Node.ELEMENT_NODE) {
                  const childTag = n.tagName?.toLowerCase();
                  if (childTag === "strong" || childTag === "b") {
                    pChildren.push(
                      new TextRun({ text: n.textContent, bold: true }),
                    );
                  } else if (childTag === "em" || childTag === "i") {
                    pChildren.push(
                      new TextRun({ text: n.textContent, italics: true }),
                    );
                  } else if (childTag === "u") {
                    pChildren.push(
                      new TextRun({ text: n.textContent, underline: {} }),
                    );
                  } else if (childTag === "br") {
                  } else {
                    Array.from(n.childNodes).forEach(processNode);
                  }
                }
              };
              Array.from(node.childNodes).forEach(processNode);
              paragraphs.push(
                new Paragraph({
                  children:
                    pChildren.length > 0
                      ? pChildren
                      : [new TextRun(text || "")],
                  spacing: { after: 200 },
                }),
              );
              break;
            case "li":
              paragraphs.push(
                new Paragraph({
                  text: `• ${text}`,
                  spacing: { after: 100 },
                }),
              );
              break;
            case "br":
              paragraphs.push(
                new Paragraph({
                  children: [new TextRun("")],
                  spacing: { after: 200 },
                }),
              );
              break;
            default:
              if (text) {
                paragraphs.push(
                  new Paragraph({
                    children: [new TextRun(text)],
                    spacing: { after: 200 },
                  }),
                );
              }
              const childParagraphs = convertHtmlToDocx(node);
              paragraphs.push(...childParagraphs);
              break;
          }
        }
      }

      return paragraphs;
    };

    const docxParagraphs = convertHtmlToDocx(tempDiv);

    const doc = new Document({
      sections: [
        {
          properties: {},
          children:
            docxParagraphs.length > 0
              ? docxParagraphs
              : [
                  new Paragraph({
                    children: [new TextRun("")],
                  }),
                ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const filename = sanitizeFilename(editorTitle, "docx");
    await saveBlobWithPlatformSupport(
      blob,
      filename,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    );
  } catch (error) {
    console.error("Error generating DOCX:", error);
    if (showError) {
      showError(
        "Failed to generate DOCX file. Please try again.",
        "Export Error",
      );
    }
  }
};
